import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useMemo } from 'react';
import type { DateWindow } from '../../lib/filters.hooks';
import { useClients } from '../../lib/filters.hooks';
import { useTRPC } from '../../lib/trpc';
import type { ScopeState } from '../filters/types';
import { PerformanceBreakdown } from './breakdown/PerformanceBreakdown';
import { TrendCard } from './breakdown/TrendCard';
import type { MediaFilters, MediaKpi } from './data/types';
import { kpiById, toScopeFilters } from './data/utils';
import { MarketLeaderboard } from './markets/MarketLeaderboard';
import { MarketPerformance } from './markets/MarketPerformance';
import type { TopMarketsSelection } from './markets/TopMarketsControl';
import { ExecutiveSummary } from './overview/ExecutiveSummary';
import { ExecutiveSummarySkeleton } from './overview/MediaBoardSkeleton';
import { MediaReportScope } from './overview/MediaReportScope';
import { NoData } from './shared/NoData';

interface MediaBoardProps {
  scope: ScopeState;
  filters: MediaFilters;
  setFilters: (filters: MediaFilters) => void;
  accountIds: number[];
  dateWindow: DateWindow;
  kpiOptions: MediaKpi[];
}

export function MediaBoard({
  scope,
  filters,
  setFilters,
  accountIds,
  dateWindow,
  kpiOptions,
}: MediaBoardProps): ReactNode {
  const trpc = useTRPC();
  const { data: clients } = useClients();
  const kpi = kpiOptions.find((option) => option.id === filters.kpi) ?? kpiById(filters.kpi);
  const selectedCountry = filters.countries.length === 1 ? (filters.countries[0] ?? null) : null;
  // No offerable KPI means every panel would chart a metric the scope can't answer.
  const hasKpi = kpiOptions.length > 0;
  const enabled = (clients?.length ?? 0) > 0 && hasKpi;

  // The market panels rank/index across all markets, so the fetched data ignores the
  // country filter; the selection filters which markets are shown client-side, and a
  // single selected country switches Market Performance to its single-market view.
  const marketsScope = useMemo(() => {
    const scopeFilters = toScopeFilters([], filters.creativeTypes, filters.objectives);
    return {
      dateRange: dateWindow,
      kpi: filters.kpi,
      topMarkets: filters.topMarkets,
      ...(scopeFilters ? { filters: scopeFilters } : {}),
      ...(accountIds.length ? { accountIds } : {}),
    };
  }, [
    dateWindow,
    filters.kpi,
    filters.topMarkets,
    filters.creativeTypes,
    filters.objectives,
    accountIds,
  ]);

  // Full board scope (honours the country filter, unlike the cross-market panels above);
  // shared by the executive summary and the breakdown/trend panels.
  const boardScope = useMemo(() => {
    const scopeFilters = toScopeFilters(
      filters.countries,
      filters.creativeTypes,
      filters.objectives,
    );
    return {
      dateRange: dateWindow,
      kpi: filters.kpi,
      ...(scopeFilters ? { filters: scopeFilters } : {}),
      ...(accountIds.length ? { accountIds } : {}),
    };
  }, [
    dateWindow,
    filters.kpi,
    filters.countries,
    filters.creativeTypes,
    filters.objectives,
    accountIds,
  ]);

  const summary = useQuery({
    ...trpc.mediaPerformance.executiveSummary.queryOptions(boardScope),
    enabled,
  });

  const topMarketsSelection: TopMarketsSelection = {
    value: filters.topMarkets,
    onChange: (topMarkets) => setFilters({ ...filters, topMarkets }),
  };

  return (
    <Box
      sx={{
        mx: { md: 10, lg: 20, xl: 50 },
        '@media print': {
          mx: 0,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          overflow: 'visible',
        },
      }}
    >
      {!hasKpi ? (
        <NoData message="None of the board KPIs are reported for the selected accounts." />
      ) : (
        <>
          <MediaReportScope
            scope={scope}
            filters={filters}
            dateWindow={dateWindow}
            accountIds={accountIds}
            kpiOptions={kpiOptions}
          />
          {summary.isError ? (
            <Alert severity="error">
              We couldn’t load the summary for this scope. Adjust the filters and try again.
            </Alert>
          ) : summary.data ? (
            <ExecutiveSummary data={summary.data} kpi={kpi} />
          ) : (
            <ExecutiveSummarySkeleton />
          )}

          <MarketPerformance
            selectedCountry={selectedCountry}
            selectedCountries={filters.countries}
            scope={marketsScope}
            enabled={enabled}
            topMarkets={topMarketsSelection}
          />
          <MarketLeaderboard
            scope={marketsScope}
            selectedCountries={filters.countries}
            enabled={enabled}
            topMarkets={topMarketsSelection}
          />
          <PerformanceBreakdown kpi={kpi} scope={boardScope} enabled={enabled} />
          <TrendCard kpi={kpi} scope={boardScope} enabled={enabled} />
        </>
      )}
    </Box>
  );
}
